import { BranchesData, emptyBranches, parseBranches } from "./branches";
import { Fix64 } from "../maths/fix64";
import { CommerceData, emptyCommerce, parseCommerce } from "./commerce";
import { EventsData, emptyEvents, parseEvents } from "./events";
import { AchievementsData, emptyAchievements, parseAchievements } from "./achievements";
import { OffersData, emptyOffers, parseOffers } from "./offers";
import { ShopsData, emptyShops, parseShops } from "./shops";
import { TrophiesData, emptyTrophies, parseTrophies } from "./trophies";
import { VipTable, emptyVipTable, parseVipTable } from "./vip";
import { PetsData, emptyPets, parsePets } from "./pets";
import { EnemyAbilities, parseEnemyAbilities } from "./enemyAbilities";
import { ModesData, emptyModes, parseModes } from "./modes";
import { ResearchData, emptyResearch, parseResearch } from "./research";
import { SpawnRulesData, emptySpawnRules, parseSpawnRules } from "./spawnRules";
import { SupplyId, isSupplyId } from "./supplies";
import { SeasonsData, emptySeasons, parseSeasons } from "./seasons";

export enum Heading { North = 0, East = 1, South = 2, West = 3 }
export enum TileKind { Normal = 0, Mud = 1, Bridge = 2, Toll = 3, FireTrap = 4 }
export enum DamageType { Physical = 0, Magic = 1, Burn = 2 }

export interface EnemyArchetype {
  readonly index: number;
  readonly id: string;
  readonly name: string;
  readonly hp: Fix64;
  readonly speedCellsPerSecond: Fix64;
  readonly armour: Fix64;
  readonly points: number;
  readonly leakDamage: number;
  readonly isAir: boolean;
  readonly unlockLevel: number;
  /** Healer: heals other enemies within this many cells by `healFractionPerSecond` of their max HP each second (0 = none). */
  readonly healRadius: Fix64;
  readonly healFractionPerSecond: Fix64;
  /** Flyer boost: below `boostThreshold` of max HP, speed ×`boostSpeedMult` for `boostDurationTicks`, once per `boostCooldownTicks` (0 = none). */
  readonly boostThreshold: Fix64;
  readonly boostSpeedMult: Fix64;
  readonly boostDurationTicks: number;
  readonly boostCooldownTicks: number;
  /** Bomber: on death or on reaching the base, the nearest tower is disabled for this many ticks (0 = none). */
  readonly bombDisableTicks: number;
  /** Shield wall: other enemies within `shieldRadius` cells gain `shieldArmour` physical armour (0 = none; never stacks). */
  readonly shieldRadius: Fix64;
  readonly shieldArmour: Fix64;
  /**
   * Bomber: how far the dropped bomb reaches, in cells from where the bomber fell — a tower whose cell is farther than
   * this is out of reach and the bomb is wasted (0 = unlimited, the pre-2026-09-22 rule).
   */
  readonly bombRange: Fix64;
  /** Boss signature mechanic (None for ordinary enemies). */
  readonly bossMechanic: BossMechanic;
  /**
   * A heavy, large unit (the Brute, the Stunner; every boss counts too, `isHeavy`): area damage — splash, chains,
   * cones, clusters, zones, damage auras and the area towers' own hits — does `areaVsHeavyMult` of itself to it (M30 #14).
   */
  readonly heavy: boolean;
  /** Summoner, Burrower, Spellward and Stunner abilities (data/enemyAbilities.ts, docs/ENEMIES_V2.md); none by default. */
  readonly abilities: EnemyAbilities;
  /**
   * A rare spawn (M36, docs/RARES.md): the chapter (1-based) whose levels it can appear on, 0 for every other enemy.
   * Rares never come in a level's groups or in Endless; the sim rolls them (sim/rares.ts).
   */
  readonly rareChapter: number;
  /**
   * The Starfall Wyrm (M40, docs/RARES.md, sim/wyrm.ts): `legend: true` keeps it out of every pool, list and filter a
   * rare is kept out of (level waves, Endless's pool, immunity, elites, the Almanac's raiders and bosses); the sim rolls it.
   */
  readonly legend: boolean;
  /**
   * A pet's wild encounter (Pets v2, docs/PETS.md, sim/wildPet.ts): the pets.json pet it is, "" for every other enemy.
   * Like the Wyrm it is kept out of every pool and filter (level waves, Endless's pool, immunity, elites, the Almanac).
   */
  readonly wildPet: string;
  /** The art the presentation draws for it when not the id's own (the wild pup wears its pet's model); "" = by id. */
  readonly model: string;
}

/** One signature mechanic per boss (DESIGN §5); the numbers live in `BossRules`. */
export enum BossMechanic { None = 0, Warlord = 1, Colossus = 2, Necromancer = 3, HarpyQueen = 4, Ironclad = 5, PlagueLord = 6 }

export function parseBossMechanic(s: string): BossMechanic {
  switch (s) {
    case "": case "none": return BossMechanic.None;
    case "warlord": return BossMechanic.Warlord;
    case "colossus": return BossMechanic.Colossus;
    case "necromancer": return BossMechanic.Necromancer;
    case "harpyQueen": return BossMechanic.HarpyQueen;
    case "ironclad": return BossMechanic.Ironclad;
    case "plagueLord": return BossMechanic.PlagueLord;
    default: throw new Error(`unknown boss mechanic '${s}'`);
  }
}

/**
 * An elite affix (DESIGN §5.1): a trait an ordinary enemy can wear on top of its archetype, from `enemies.json` →
 * `affixes`. Which spawns wear one is a level's elite rule (`LevelData.elites`) or a run modifier (sim/modifiers.ts).
 * Every field is a multiplier or a count, so an affix with the defaults changes nothing.
 */
export interface EnemyAffixDef {
  readonly index: number;
  readonly id: string;
  readonly name: string;
  /** Damage taken, per damage type (1 = unchanged; 0 = immune). A burn-immune affix also never catches fire. */
  readonly physicalTaken: Fix64;
  readonly magicTaken: Fix64;
  readonly burnTaken: Fix64;
  /** Tower slows (Frost, Deep Freeze) are this fraction as strong on the wearer (1 = unchanged). */
  readonly slowMult: Fix64;
  /** On death by damage (not on a leak): `splitCount` enemies of `splitInto` (an archetype index, −1 none) appear where it fell. */
  readonly splitInto: number;
  readonly splitCount: number;
  /** An elite's HP and kill points against a plain spawn of its archetype. */
  readonly hpMult: Fix64;
  readonly pointsMult: Fix64;
}

/**
 * Which spawns of a stage wear an affix: every `every`-th ordinary spawn (bosses never), counted in spawn order within
 * the stage, from stage `fromStage` on. Deterministic from the schedule alone, so the wave preview can show them.
 */
export interface EliteRule { readonly affix: number; readonly every: number; readonly fromStage: number }

export interface TowerTier {
  readonly damageMult: Fix64;
  readonly rangeBonus: Fix64;
  /** Barracks: its soldiers' armour at this tier (a blocked enemy's blow is cut by it); 0 elsewhere. */
  readonly soldierArmour: Fix64;
}

export interface TowerArchetype {
  readonly index: number;
  readonly id: string;
  readonly name: string;
  readonly range: Fix64;
  readonly damage: Fix64;
  readonly fireIntervalTicks: number;
  readonly targetsGround: boolean;
  readonly targetsAir: boolean;
  readonly splashRadius: Fix64;
  readonly damageType: DamageType;
  readonly unlockLevel: number;
  readonly onePerMap: boolean;
  /** The base's own attack: placed on the base cell at level start, never offered, never sold. */
  readonly isBase: boolean;
  readonly tiers: readonly TowerTier[];
  readonly slowFraction: Fix64;
  readonly slowDurationTicks: number;
  readonly burnDamagePerSecond: Fix64;
  readonly burnDurationTicks: number;
  /** Burns from this many different towers of the type add up on one enemy (DESIGN §6.1); a further one takes over the stack closest to running out. */
  readonly burnMaxStacks: number;
  readonly chainTargets: number;
  /**
   * An area tower (M30 #14): every hit it deals counts as area damage against heavy enemies, its primary target included.
   * `area` in towers.json, else derived: a damaging tower with a splash or a chain (Cannon, Mortar, Lightning).
   */
  readonly area: boolean;
  readonly chainRange: Fix64;
  readonly chainFalloff: Fix64;
  readonly pierceCount: number;
  readonly pierceWidth: Fix64;
  readonly shredPerHit: Fix64;
  readonly shredMax: number;
  readonly auraDamageBonus: Fix64;
  readonly auraPointsBonus: Fix64;
  /** Mortar (TOWERS_V2): no target nearer than this (cells); the shell is lobbed and lands `shellFlightTicks` later. */
  readonly minRange: Fix64;
  readonly shellFlightTicks: number;
  /** Barracks: soldiers kept posted on the road (0 = not a barracks), their stats at tier 1 and level 1, reach and respawn. */
  readonly soldiers: number;
  readonly soldierHp: Fix64;
  readonly soldierReach: Fix64;
  readonly soldierRespawnTicks: number;
  /** Arcane Spire: the beam's damage multiplier at full ramp (1 = no ramp) and the ticks on one target it takes to get there. */
  readonly rampMax: Fix64;
  readonly rampTicks: number;
  /** Hex Totem: extra damage taken by an enemy inside the aura at tier 1 (× the tier's damageMult), and the healing cut there. */
  readonly hexDamageTaken: Fix64;
  readonly hexHealCut: Fix64;
  /** What each tower level past 1 adds to a damageless tower's own effect (towers.json `levelGrowth`; sim/levelGrowth.ts). */
  readonly levelGrowth: TowerLevelGrowth;
}

/** Per tower level past 1 (2026-10-24): Frost's slow and its length (to a cap), the Beacon's aura bonus, the Hex curse. */
export interface TowerLevelGrowth {
  readonly slowFraction: Fix64;
  readonly slowTicks: number;
  readonly slowFractionMax: Fix64;
  readonly auraDamageBonus: Fix64;
  readonly hexDamageTaken: Fix64;
}

/** A Barracks: no shot of its own, soldiers instead (TOWERS_V2). */
export function isBarracks(t: TowerArchetype): boolean { return t.soldiers > 0; }
/** A Hex Totem: a curse aura on enemies, no damage (TOWERS_V2). */
export function isHex(t: TowerArchetype): boolean { return t.hexDamageTaken.isPositive(); }
/** A Mortar: lobbed shells with a blind spot (TOWERS_V2). */
export function isMortar(t: TowerArchetype): boolean { return t.shellFlightTicks > 0; }
/** An Arcane Spire: a ramping beam (TOWERS_V2). */
export function isBeam(t: TowerArchetype): boolean { return t.rampTicks > 0; }

export function isAura(t: TowerArchetype): boolean {
  return t.auraDamageBonus.isPositive() || t.auraPointsBonus.isPositive();
}

export interface ShapeTile { readonly x: number; readonly y: number }

export interface PathShape {
  readonly index: number;
  readonly id: string;
  readonly name: string;
  /** Local frame: open end at (0,0), forward +Y, right +X. First tile is (0,1). */
  readonly tiles: readonly ShapeTile[];
  readonly weight: number;
  readonly unlockLevel: number;
  readonly tileKind: TileKind;
  /** 0 = unlimited. */
  readonly maxPerLevel: number;
  /**
   * Road pace the segment counts for (DESIGN §4.4, §4.7): road cards come less often the more pace is laid. Its cell
   * count unless `cards.json` says otherwise. Special tiles trade on it: a Bridge is cheap road (2 cells for 1), Mud
   * dear (3 cells for 4), so the next road card comes sooner after a Bridge and later after Mud.
   */
  readonly paceCost: number;
}

export class CardsData {
  constructor(
    readonly offerSize: number,
    readonly openingPathOffers: number,
    readonly openingTowerOffers: number,
    readonly offerPointsBase: number,
    readonly offerPointsGrowth: Fix64,
    readonly rerollEveryWaves: number,
    readonly rerollBank: number,
    readonly maxSameTowerPerOffer: number,
    readonly towerCardWeight: number,
    readonly shapes: readonly PathShape[],
    /** Scarcity (DESIGN §4.4): how many different tower types a tower-bearing in-run offer shows. */
    readonly maxTowerCardsPerOffer = 1,
    /**
     * In-run offers carry tower cards once every N offers, N read from this list at the number of towers standing
     * (the last entry repeats): the more towers stand, the longer the wait for the next.
     */
    readonly towerOfferIntervals: readonly number[] = [1],
    /** An Upgrade card is offered once every this many in-run offers. */
    readonly upgradeOfferInterval = 1,
    /** Points added to the next offer's threshold when a card of this kind is placed from an in-run offer. */
    readonly pickPointsCost: { readonly path: number; readonly tower: number; readonly upgrade: number } = { path: 0, tower: 0, upgrade: 0 },
    /** Special-tile effects (DESIGN §4.7): ground speed multiplier on Mud; kill-points multiplier after passing a Toll gate. */
    readonly mudSpeedMult: Fix64 = Fix64.parse("0.7"),
    readonly tollPointsMult: Fix64 = Fix64.parse("1.1"),
    /**
     * The road pace (DESIGN §4.4): past the level's `roadPaceTilesAt`, a road piece may follow the last one every
     * `firstInterval`-th in-run offer, one offer further apart for every `intervalStepTiles` more pace laid, at most every
     * `maxInterval`-th. Never 0: the road never finishes, and this decay is what keeps it from growing long.
     */
    readonly roadPace: { readonly firstInterval: number; readonly intervalStepTiles: number; readonly maxInterval: number } = { firstInterval: 2, intervalStepTiles: 1, maxInterval: 2 },
    /**
     * The Move card (M55, DESIGN §4.4): drawn in an in-run hand's road slots beside the road shapes, at `weight` against
     * theirs, once `minTowers` towers stand (the fortress not counted) and a square is free to move one to. Placing it
     * adds `pointsCost` to the next offer's threshold. Weight 0: never dealt.
     */
    readonly moveCard: { readonly weight: number; readonly minTowers: number; readonly pointsCost: number } = { weight: 0, minTowers: 3, pointsCost: 0 },
  ) {}

  /** In-run offers between two road-bearing offers once `laid` road pace is down, on a level whose pace knee is `paceTiles`. */
  roadOfferInterval(laid: number, paceTiles: number): number {
    if (laid < paceTiles) return 1;
    const past = Math.floor((laid - paceTiles) / this.roadPace.intervalStepTiles);
    return Math.min(this.roadPace.maxInterval, this.roadPace.firstInterval + past);
  }

  /** Offers between two tower-bearing in-run offers with `built` towers standing. */
  towerOfferInterval(built: number): number {
    const list = this.towerOfferIntervals;
    return list[Math.min(built, list.length - 1)];
  }

  /** Points needed to open the n-th post-opening offer (1-based): cumulative sum of base × growth^(k-1). Integer, deterministic. */
  offerThreshold(n: number): number {
    let total = 0;
    let gap = Fix64.fromInt(this.offerPointsBase);
    for (let k = 1; k <= n; k++) {
      total += gap.floorToInt();
      gap = gap.mul(this.offerPointsGrowth);
    }
    return total;
  }
}

export interface EconomyData {
  readonly baseHp: number;
  readonly betweenStageTicks: number;
  readonly firstStageDelayTicks: number;
  /** The boss walks out this many ticks after the final stage's last scheduled spawn. */
  readonly bossDelayTicks: number;
  readonly bossHpMult: Fix64;
  readonly bossPoints: number;
  /** Minimum base damage a boss does when it reaches the base. */
  readonly bossLeakDamage: number;
  /** Calling the next stage early pays this many points per second of countdown left (DESIGN §4.8.7). */
  readonly earlyCallPointsPerSecond: Fix64;
  /** What area damage does to a heavy enemy (EnemyArchetype.heavy, bosses), as a fraction of itself (M30 #14); 1 = no rule. */
  readonly areaVsHeavyMult: Fix64;
  /** Skipping an in-run card offer pays this many points (DESIGN §4.8.7). */
  readonly skipOfferPoints: number;
  /**
   * Checkpoint retry (DESIGN §4.8.8): a lost run may restart, once, from the break after every this-many stages
   * cleared, or from the break before the boss stage (0 = no checkpoints).
   */
  readonly checkpointEveryStages: number;
  readonly troops: TroopsData;
}

/** Base troops (DESIGN §7): stats at base level 1, growth per base level, spawn cadence anchors, blocking rules. */
export interface TroopsData {
  readonly hp: Fix64;
  readonly damage: Fix64;
  readonly attackIntervalTicks: number;
  readonly cap: number;
  readonly speedCellsPerSecond: Fix64;
  readonly engageRange: Fix64;
  readonly statGrowth: Fix64;
  readonly spawnIntervalTicksAtL1: number;
  readonly spawnIntervalTicksAtMaxLevel: number;
  /** A blocked ground enemy hits the troop for this fraction of its own max HP every `enemyAttackIntervalTicks`. */
  readonly enemyHitFraction: Fix64;
  readonly enemyAttackIntervalTicks: number;
  /** Chebyshev distance (cells) within which a troop stops a ground enemy. */
  readonly blockRange: Fix64;
  /** Health and damage added per in-run fortress tier above the first (0.25: tier 2 ×1.25, tier 3 ×1.5; troopTierMult). */
  readonly baseTierBonus: Fix64;
  /** Cells between neighbours in the troops' formation at the flag (formationSlot). */
  readonly formationSpacing: Fix64;
  /** Rows in that formation, front to back; its width is the troop cap over this, rounded up. */
  readonly formationRows: number;
  /** Cells between neighbours ACROSS the road in that formation (the line's width; `formationSpacing` is between rows). */
  readonly formationLateralSpacing: Fix64;
}

export enum Rarity { Common = 0, Uncommon = 1, Rare = 2, Epic = 3, Legendary = 4, Mythic = 5 }
export const RARITY_IDS = ["common", "uncommon", "rare", "epic", "legendary", "mythic"] as const;
/** Battle chest bands (§3.3). Farm / FarmNoLeak: a win of a level already cleared (docs/SHOP.md §3.3 K3; appended, so
 *  the replay hash's band numbers of the first four hold). */
export enum ChestBand { Loss = 0, Win = 1, WinNoLeak = 2, FirstClear = 3, Farm = 4, FarmNoLeak = 5 }
export const BAND_IDS = ["loss", "win", "winNoLeak", "firstClear", "farm", "farmNoLeak"] as const;
/** What a bundle without the farm bands pays for them: the plain win bands (an old bundle, a test fixture). */
const BAND_FALLBACK: Partial<Record<(typeof BAND_IDS)[number], (typeof BAND_IDS)[number]>> = { farm: "win", farmNoLeak: "winNoLeak" };

export interface MetaData {
  /** Gold for a tower/base upgrade before the level curve (DESIGN §8, §9.3). */
  readonly upgradeGoldBase: number;
  readonly reductionSecondsPerLevel: number;
  readonly reductionsPerDay: number;
  readonly powerTowerWeight: number;
  readonly powerBaseWeight: number;
  /**
   * @deprecated Builders are real money only since 2026-10-19 (docs/SHOP.md §18; meta/builders.ts): always empty, no
   * builder has a gem price. Kept so older callers compile; gen.py rejects the key in meta.json.
   */
  readonly builderCostsGems: readonly number[];
  /** Fraction of the spent resources returned when a running upgrade is cancelled (§8). */
  readonly cancelRefundFraction: Fix64;
  /** The free wall's steeper upgrade costs (docs/SHOP.md §3.3 K1; `upgradeCostMult`); null: the plain curve. */
  readonly upgradeCostCurve: UpgradeCostCurveData | null;
}

export interface UpgradeCostCurveData {
  /** First level (upgraded to) the curve applies to. */
  readonly fromLevel: number;
  /** Levels over which `step` phases in. */
  readonly rampLevels: number;
  readonly step: Fix64;
  /** Per level past `fromLevel` (negative: the extra eases off late). */
  readonly slopePerLevel: Fix64;
}

/**
 * The upgrade resource multiplier for an upgrade to `toLevel` (docs/SHOP.md §3.3 K1, meta.json upgradeCostCurve):
 * × 1 below `fromLevel`, then 1 + step × min(1, (L − from + 1) / rampLevels) + slopePerLevel × (L − from), never
 * under × 1. Levels 10/11/12/30/60 read × 1.75 / 2.485 / 3.22 / 2.95 / 2.5.
 */
export function upgradeCostMult(meta: MetaData, toLevel: number): Fix64 {
  const c = meta.upgradeCostCurve;
  if (c === null || toLevel < c.fromLevel) return Fix64.One;
  const into = toLevel - c.fromLevel;
  const ramp = c.step.mulInt(Math.min(into + 1, c.rampLevels)).divInt(c.rampLevels);
  return Fix64.max(Fix64.One, Fix64.One.add(ramp).add(c.slopePerLevel.mulInt(into)));
}

/**
 * The hub's readiness strip (DESIGN §3.4): the per-level yardstick the tuning assumed — precomputed at build time by
 * tools/balance/readiness.ts, never derived at runtime — and the calibrated bands the app reads it with. Presentation
 * data only: nothing in the sim reads it.
 */
export interface ReadinessData {
  /** First level the strategic player needs gear on (DESIGN §3.4). */
  readonly gearRequiredFrom: number;
  /** Ready when equipped gear is at least this percent of the level's target… */
  readonly readyGearPercent: number;
  /** …and the roster's mean level at least this percent of its target. */
  readonly readyTowerPercent: number;
  /** Under-geared when even the best loadout owned stays below this percent of the gear target… */
  readonly underGearPercent: number;
  /** …or the roster's mean level is below this percent of its target. */
  readonly underTowerPercent: number;
  /**
   * The towers' band also counts levels under parity (W2's M6 finding: a percentage lets a late player sit 10–12
   * levels under): Ready needs the mean level within this many levels of the target as well as readyTowerPercent…
   */
  readonly readyTowerLevelsUnder: number;
  /** …and more than this many levels under reads Under-geared, as underTowerPercent does. */
  readonly underTowerLevelsUnder: number;
  /** Per level (index = level − 1): the loadout the level was tuned with. */
  readonly targets: readonly ReadinessTargetData[];
}

export interface ReadinessTargetData {
  /** Gear score (§7) of the expected inventory equipped the sensible way. */
  readonly gear: number;
  /** Persistent tower and base level it was tuned at (parity: the level number). */
  readonly towers: number;
  /** Per gear slot index: tower archetype indexes that slot's items go on for this level, best item first; [] for base slots. */
  readonly seats: readonly (readonly number[])[];
}

export interface ChestsData {
  /** Per band, weights per rarity in permille-precision integers (sum 100000). */
  readonly bandWeights: readonly (readonly number[])[];
  readonly rarityMult: readonly Fix64[];
  readonly resourceBase: number;
  readonly resourcePerLevel: number;
  /** Per rarity: [min, max] gear items in the chest (§9.3). */
  readonly gearCount: readonly (readonly [number, number])[];
  /** Per rarity: minutes of speed-up in the chest (§9.3). */
  readonly speedUpMinutes: readonly number[];
  /** Extra gear items in a First-clear chest (DESIGN §9.3): the first clear of each level always pays in gear. */
  readonly firstClearExtraGear: number;
  /** Farm-run chests (docs/SHOP.md §3.3 K3): resources × this… */
  readonly farmResourceMult: Fix64;
  /** …and one more gear item on this percent of them. */
  readonly farmExtraGearPercent: number;
  /** From this level a first clear pays `lateFirstClearBand` instead (K2; `battleChestBand`); 0: never. */
  readonly lateFirstClearFrom: number;
  readonly lateFirstClearBand: ChestBand;
  /** MP-25: a late first clear's rolled rarity is raised this many steps (capped at Mythic); 0: none. */
  readonly lateFirstClearRarityBump: number;
  /** The fixed Starter chest (§9.3): what the first clear of `starter.level` pays instead of a roll; null for none. */
  readonly starter: StarterChestData | null;
  /** Early resource boost (§9.3): chest resources × this through `earlyFullThroughLevel`… */
  readonly earlyResourceMult: Fix64;
  readonly earlyFullThroughLevel: number;
  /** …tapering linearly to × 1 at this level. */
  readonly earlyTaperToLevel: number;
  /** Seat-targeted early first-clear gear (§9.3, §3.4); null for none. */
  readonly earlyGear: EarlyGearData | null;
  /**
   * The rarity climb (DESIGN §9.2, M30 #12): per rarity, the chest level it can first roll at (`climbFrom`) and the
   * level from which it rolls at its full band weight (`climbFull`); from the first its weight grows linearly and what it
   * has not yet earned falls to the rarity below (`bandWeightsAt`). Common: 1 / 1. No `rarityClimb`: every rarity at once.
   */
  readonly climbFrom: readonly number[];
  readonly climbFull: readonly number[];
}

export interface StarterChestData {
  readonly level: number;
  readonly rarity: Rarity;
  readonly gold: number;
  readonly gearCount: number;
}

export interface EarlyGearData {
  /** First-clear chests of levels 1..throughLevel get the floors below and seat-targeted gear… */
  readonly throughLevel: number;
  /** …rolled for the free seats of this level's readiness target. */
  readonly forLevel: number;
  readonly minRarity: Rarity;
  /** This level's first-clear chest is at least Rare (0 = none). */
  readonly rareAtLevel: number;
}

/** Where an order is done (orders.json): a ~1 minute task in the lobby, or counted from battles' sim events. */
export type OrderKind = "hub" | "battle";

/** One Daily Order template (balance/src/orders.json `pool`). */
export interface OrderTemplate {
  readonly id: string;
  readonly kind: OrderKind;
  /** What counts: a metric name, `kills`/`wins` with a param (enemy id, `air`, `any`, `noLeak`, `$tower`). */
  readonly metric: string;
  readonly target: number;
  /** Reward size relative to an average order (the day's pool averages about 1). */
  readonly size: Fix64;
  readonly speedUpMinutes: number;
  /** Highest cleared level the order needs (its enemy or feature has been met). */
  readonly minLevel: number;
  /** Display copy: `{n}` = target, `{tower}` = the picked tower's name. */
  readonly title: string;
  /** MP-06: a pre-battle supply the order also pays (absent: none). */
  readonly supply?: SupplyId;
}

/** A check-in slot or the welcome-back bundle: a fraction of an average Win chest, a speed-up, or a chest of a rarity. */
export interface CheckinRewardData {
  readonly resources: Fix64;
  readonly speedUpMinutes: number;
  /** A chest of this rarity at the player's level, or null. */
  readonly chestRarity: Rarity | null;
  /** Gems paid with it (D20: the check-in's weekly milestones; MP-08: day 1). */
  readonly gems: number;
  /** Fusion Sigils of this rarity paid with it (MP-24: the day-14 milestone), or null. */
  readonly sigil: Rarity | null;
  readonly sigils: number;
}

/** Daily Orders and the check-in calendar (ENGAGEMENT_2026-09-23 §B5); the sizing arithmetic is in orders.json `_why`. */
export interface OrdersData {
  readonly unlockAfterLevel: number;
  readonly ordersPerDay: number;
  /** One day of orders pays about this many average Win chests of resources. */
  readonly chestsPerDay: Fix64;
  readonly carryDays: number;
  readonly maxCarried: number;
  readonly freeRerollsPerDay: number;
  readonly weeklyGoal: number;
  readonly supplyBand: ChestBand;
  readonly weeklyBand: ChestBand;
  /** Gems the weekly chest pays on top of its roll (D20). */
  readonly weeklyGems: number;
  /** Forge Essence the weekly chest adds (gear v2 free routes, docs/GEAR_V2.md). */
  readonly weeklyEssence: number;
  readonly pool: readonly OrderTemplate[];
  readonly checkinSlots: readonly CheckinRewardData[];
  readonly welcomeBackAfterDays: number;
  readonly welcomeBack: CheckinRewardData;
}

/**
 * One line of a level's wave template (DESIGN §3.2). A group spawns `count` enemies `intervalTicks` apart, `delayTicks`
 * into the stage; the count grows linearly from `fromStage`. Structure beyond a plain horde comes from the optional
 * fields: `untilStage` retires a group, `everyStages` makes it a periodic raid (every Nth stage counted from
 * `fromStage`), and `repeats` sends the same clump again `repeatGapTicks` later, so one stage arrives as several
 * surges rather than one blob.
 */
export class SpawnGroup {
  constructor(
    readonly enemyIndex: number,
    readonly baseCount: number,
    readonly countPerStage: Fix64,
    readonly intervalTicks: number,
    readonly delayTicks: number,
    readonly fromStage: number,
    /** Last stage the group appears in (0 = every stage to the end). */
    readonly untilStage = 0,
    /** Appears on stages fromStage, fromStage + everyStages, … (1 = every stage). */
    readonly everyStages = 1,
    /** How many times the clump is sent within one stage. */
    readonly repeats = 1,
    /** Ticks between the starts of two repeats. */
    readonly repeatGapTicks = 0,
    /**
     * Multiplier on the archetype's speed for this group's spawns (1 = as the archetype). The first level's runners
     * walk at 0.75 (tools/levels/waves.py FIRST_LEVEL_RUNNER_SPEED) so the first fast enemy a player meets is readable.
     */
    readonly speedMult: Fix64 = Fix64.One,
  ) {}

  /** Whether the group spawns at all on this stage. */
  activeAt(stage: number): boolean {
    if (stage < this.fromStage) return false;
    if (this.untilStage > 0 && stage > this.untilStage) return false;
    return (stage - this.fromStage) % Math.max(1, this.everyStages) === 0;
  }

  /** Enemies in one clump on this stage (0 when the group is inactive). */
  countAtStage(stage: number): number {
    if (!this.activeAt(stage)) return 0;
    return this.baseCount + this.countPerStage.mulInt(stage - this.fromStage).floorToInt();
  }

  /** Enemies the group sends over the whole stage, all repeats included. */
  totalAtStage(stage: number): number { return this.countAtStage(stage) * Math.max(1, this.repeats); }
}

/**
 * Authored look for a level: which terrain profile the ground uses, which of its chasms are molten, and the hero
 * props placed by hand.
 *
 * Everything here is presentation. The chasms themselves are NOT — they live in `LevelData.chasm` because the road
 * can be routed across them, which the simulation has to know about. This block only decides what the bottom of one
 * looks like.
 */
export interface LevelTerrain {
  /** Name of a `Terrain.Profiles` entry; an unknown name falls back to the level-number rotation. */
  readonly profile: string;
  /** Which of the level's chasm cells are molten rather than rubble-floored. A subset of `chasm`. */
  readonly lava: readonly ShapeTile[];
  /** Hero props by model name, placed on a named cell (which must be blocked, or the road could run through it). */
  readonly props: readonly LevelProp[];
}

export interface LevelProp { readonly name: string; readonly x: number; readonly y: number }

function parseLevelTerrain(v: Json | null): LevelTerrain | null {
  if (v === null || v === undefined) return null;
  const tiles = (k: string): ShapeTile[] => (opt(v, k, []) as Json[]).map((p) => ({ x: int(p[0]), y: int(p[1]) }));
  return {
    profile: str(opt(v, "profile", "downs")),
    lava: tiles("lava"),
    props: (opt(v, "props", []) as Json[]).map((p) => ({ name: str(req(p, "name")), x: int(req(p, "x")), y: int(req(p, "y")) })),
  };
}

/**
 * The third-star challenge of a level (`levels.json` → `challenge`): one extra condition on a win, read from the run's
 * command tally and final state by `sim/stars.ts`. Never part of the simulation, so goldens don't see it.
 */
export enum ChallengeKind {
  /** Place at most `n` towers (every placement counts, sold or not). */
  MaxTowers = 0,
  /** Never place a tower of archetype `n` (a tower owned by the time the level unlocks). */
  NoTower = 1,
  /** End with the fortress at `n` % of its HP or more (100: take no damage at all). */
  MinHp = 2,
  /** Apply at most `n` upgrade cards. */
  MaxUpgrades = 3,
  /** Never spend a reroll charge. */
  NoReroll = 4,
  /** Place at most `n` road cards (the opening draft's included). */
  MaxRoad = 5,
  /** Never skip a card offer. */
  NoSkip = 6,
  /** Call at least `n` waves early (NEXT WAVE before the countdown ends). */
  EarlyCalls = 7,
}

/** JSON names of the challenge kinds, in `ChallengeKind` order. */
export const CHALLENGE_KIND_IDS = ["maxTowers", "noTower", "minHp", "maxUpgrades", "noReroll", "maxRoad", "noSkip", "earlyCalls"] as const;

/** `n` is the kind's number: a count, a percentage, or (NoTower) the tower archetype index. Unused kinds keep 0. */
export interface LevelChallenge { readonly kind: ChallengeKind; readonly n: number }

/** The upper bound of a percentage challenge (MinHp). */
const PERCENT_MAX = 100;

function parseChallenge(v: Json | null, towers: readonly TowerArchetype[], level: number): LevelChallenge | null {
  if (v === null || v === undefined) return null;
  const id = str(req(v, "kind"));
  const kind = (CHALLENGE_KIND_IDS as readonly string[]).indexOf(id) as ChallengeKind;
  if (kind < 0) throw new Error(`level ${level}: unknown challenge kind '${id}'`);
  if (kind === ChallengeKind.NoTower) {
    const tower = towers.find((t) => t.id === str(req(v, "tower")) && !t.isBase);
    if (!tower) throw new Error(`level ${level}: challenge names unknown tower '${str(req(v, "tower"))}'`);
    if (tower.unlockLevel > level) throw new Error(`level ${level}: challenge bans ${tower.id}, which unlocks later`);
    return { kind, n: tower.index };
  }
  const n = int(opt(v, "n", 0));
  if (n < 0 || (kind === ChallengeKind.MinHp && (n <= 0 || n > PERCENT_MAX))) throw new Error(`level ${level}: challenge ${id} n=${n} out of range`);
  return { kind, n };
}

export interface LevelData {
  readonly id: number;
  readonly name: string;
  readonly width: number;
  readonly height: number;
  readonly baseX: number;
  readonly baseY: number;
  /** Direction the road leaves the base in; the first segment must go this way. */
  readonly baseHeading: Heading;
  readonly blocked: readonly ShapeTile[];
  /**
   * Cells that are a gap in the ground. The road MAY cross one — that is what puts a bridge there — but nothing can
   * be built on it and nothing may stand on it unbridged. Must not overlap `blocked` or the fortress.
   */
  readonly chasm: readonly ShapeTile[];
  readonly groups: readonly SpawnGroup[];
  readonly bossEnemyIndex: number;
  /**
   * The level's own boss HP multiplier and the ticks between the final stage's last spawn and its boss (`waves` →
   * `bossHpMult`, `bossDelayTicks`); null = the economy's. Level 1's tutorial boss is weaker and walks in alone
   * (TOWERS_V2 / D2). Read them through `bossHpMultOf` / `bossDelayOf`.
   */
  readonly bossHpMult: Fix64 | null;
  readonly bossDelayTicks: number | null;
  /** The level's own stage count (`levels.json` → `stages`), null = the curve's (`tables.stagesAt`); read via `BalanceData.stagesOf`. */
  readonly stages: number | null;
  /**
   * The level's own stage power table (M38, DESIGN §3.2): stage s multiplies enemy HP by `stagePower[s − 1]`, written by
   * tools/balance/gen.py from `levels.json` → `stagePowerGrowth` (a long siege's power climbs more gently per stage than
   * the curve's 1.05, so its last stage is not out of reach); null = the curve's (`tables.stagePowerAt`). Read via
   * `stagePowerOf`.
   */
  readonly stagePower: readonly Fix64[] | null;
  /**
   * Waves overlap (DESIGN §3.2, M30 #19): this many ticks after a stage's last spawn the next stage starts on its own
   * while raiders of the last are still on the road (`levels.json` → `overlapTicks`, written by tools/levels/waves.py
   * from its curve); 0 = the next stage waits for the clear and the countdown, as levels 1-10 do.
   */
  readonly overlapTicks: number;
  /** Hand-authored landscape, or null for a level whose ground is generated from its seed. */
  readonly terrain: LevelTerrain | null;
  /** The third-star challenge, or null when the level has none (the third star is then out of reach). */
  readonly challenge: LevelChallenge | null;
  /** Elite affix rules (DESIGN §5.1): the level's own `elite` entry, else its block's while `levels.json` elites are enabled. */
  readonly elites: readonly EliteRule[];
  /**
   * ★2: the share of its HP (percent) the fortress must keep to the end (`levels.json` → `starHpPercent`, D15). The
   * teaching levels ask less, because they are tuned to end well below full HP; absent = DEFAULT_STAR_HP_PERCENT.
   */
  readonly starHpPercent: number;
  /**
   * Campaign difficulty (M56): every enemy's HP on a campaign run of this level is multiplied by it — the level's own
   * `hpMult`, else `levels.json` → `hpMult` (`value` from `fromLevel`), else 1. Iron, Endless, Daily Road, event runs and
   * challenge codes keep their own power (sim/waveSchedule.ts).
   */
  readonly hpMult: Fix64;
}

/** ★2's fortress-HP threshold where a level sets none (percent; D15: levels 11+). */
export const DEFAULT_STAR_HP_PERCENT = 75;

/** The boss HP multiplier on `level`: its own, else the economy's. */
export function bossHpMultOf(balance: BalanceData, level: LevelData): Fix64 { return level.bossHpMult ?? balance.economy.bossHpMult; }
/** Enemy power at `stage` of `level` (DESIGN §3.2): the level's own stage power table, else the curve's. */
export function stagePowerOf(balance: BalanceData, level: LevelData, stage: number): Fix64 {
  const own = level.stagePower;
  if (own === null) return balance.tables.stagePowerAt(stage);
  if (!Number.isInteger(stage) || stage < 1 || stage > own.length) throw new RangeError(`stage ${stage} must be 1..${own.length}`);
  return own[stage - 1];
}
/** Ticks from the final stage's last spawn to its boss on `level`: its own, else the economy's. */
export function bossDelayOf(balance: BalanceData, level: LevelData): number { return level.bossDelayTicks ?? balance.economy.bossDelayTicks; }

function parseStarHpPercent(v: Json, level: number): number {
  const pct = int(v);
  if (pct <= 0 || pct > PERCENT_MAX) throw new Error(`level ${level}: starHpPercent ${pct} out of range 1-${PERCENT_MAX}`);
  return pct;
}

export class BalanceTables {
  constructor(
    private readonly stagePower: readonly Fix64[],
    private readonly levelBase: readonly Fix64[],
    private readonly levelMult: readonly Fix64[],
    private readonly timerSeconds: readonly number[],
    private readonly roadPaceTiles: readonly number[],
    private readonly stages: readonly number[],
    /**
     * The gold cost curve of a persistent level (M38, curves.json `upgradeCostGrowth`): the cost is its own curve now, so
     * a steeper damage per level (`levelMult`) does not also make every upgrade dearer. Absent (an older bundle): levelMult.
     */
    private readonly upgradeCost: readonly Fix64[] = levelMult,
  ) {}

  get maxLevel(): number { return this.levelBase.length; }
  get maxStages(): number { return this.stagePower.length; }

  private static check(v: number, max: number, what: string): number {
    if (!Number.isInteger(v) || v < 1 || v > max) throw new RangeError(`${what} ${v} must be 1..${max}`);
    return v;
  }

  stagePowerAt(stage: number): Fix64 { return this.stagePower[BalanceTables.check(stage, this.stagePower.length, "stage") - 1]; }
  levelBaseAt(level: number): Fix64 { return this.levelBase[BalanceTables.check(level, this.levelBase.length, "level") - 1]; }
  levelMultAt(level: number): Fix64 { return this.levelMult[BalanceTables.check(level, this.levelMult.length, "level") - 1]; }
  /** The gold cost multiplier of persistent level `level` (PlayerProfile.costFor). */
  upgradeCostAt(level: number): Fix64 { return this.upgradeCost[BalanceTables.check(level, this.upgradeCost.length, "level") - 1]; }
  timerSecondsAt(level: number): number { return this.timerSeconds[BalanceTables.check(level, this.timerSeconds.length, "level") - 1]; }
  /** Road pace up to which every in-run offer carries road cards (DESIGN §4.4): 13 + floor(L/6), capped. */
  roadPaceTilesAt(level: number): number { return this.roadPaceTiles[BalanceTables.check(level, this.roadPaceTiles.length, "level") - 1]; }
  stagesAt(level: number): number { return this.stages[BalanceTables.check(level, this.stages.length, "level") - 1]; }
}

/** Gear tables (DESIGN §9.4, §9.6). */
export enum GearStat { Damage = 0, Range = 1, FireRate = 2, Hp = 3, Points = 4 }
export const GEAR_STAT_IDS = ["damage", "range", "fireRate", "hp", "points"] as const;
export enum GearTarget { Tower = 0, Base = 1 }
export interface GearSlotDef { readonly index: number; readonly id: string; readonly name: string; readonly target: GearTarget; readonly primary: GearStat }
export interface GearAffixDef { readonly stat: GearStat; readonly value: Fix64 }
/** `value` is the effect's one number where it has one (extra chain hops, pierce, shred stacks, cells of spread or range). */
export interface MythicEffectDef { readonly index: number; readonly id: string; readonly name: string; readonly towerId: string; readonly description: string; readonly value: Fix64 }
export interface GearData {
  readonly slots: readonly GearSlotDef[];
  readonly primaryBase: readonly Fix64[];
  readonly primaryRarityMult: readonly Fix64[];
  readonly affixCount: readonly number[];
  readonly affixes: readonly GearAffixDef[];
  /** Item level (gear v2, docs/GEAR_V2.md): the stat factor is 1 + growthPerLevel × (level − 1). */
  readonly itemLevel: ItemLevelData;
  /** Forge Essence: raise costs, salvage and chest essence (gear.json `essence`). */
  readonly essence: EssenceData;
  /** Fusion Sigils a fusion may use in place of fodder (gear.json `fusion.maxSigils`). */
  readonly fusionMaxSigils: number;
  /** Per result rarity: the Forge Essence a fusion costs (index 0, Common, is never a result). */
  readonly fusionEssence: readonly number[];
  /** An item binds once it is worn and has been raised more than this many levels with essence (the old "+3"). */
  readonly bindAboveRaised: number;
  readonly setPieces: number;
  readonly setMinRarity: Rarity;
  readonly setDamage: Fix64;
  readonly setRange: Fix64;
  readonly mythicEffects: readonly MythicEffectDef[];
  readonly salvageBase: readonly number[];
  readonly auctionMinPrice: readonly number[];
  readonly powerRarity: readonly number[];
  /** Per tower id: the stat that tower answers best to (DESIGN §9.4); an item whose primary matches counts `affinityMult` times. */
  readonly affinity: ReadonlyMap<string, GearStat>;
  readonly affinityMult: Fix64;
  /** Per tower id: gear stats that tower cannot use, which count as its affinity stat on it (gear.json `retarget`; meta/gear.ts statOn). */
  readonly retarget: ReadonlyMap<string, readonly GearStat[]>;
}

export interface ItemLevelData {
  readonly growthPerLevel: Fix64;
  /** A drop arrives at the chest level minus 0..dropSpread (never below 1). */
  readonly dropSpread: number;
  readonly maxLevel: number;
}

export interface EssenceData {
  /** Per rarity: essence to raise from level L to L + 1 is raise × (1 + L / raiseLevelDivisor), rounded up. */
  readonly raise: readonly number[];
  readonly raiseLevelDivisor: number;
  /** Per rarity: salvage essence before the level scaling (1 + level / salvageLevelDivisor). */
  readonly salvage: readonly number[];
  readonly salvageLevelDivisor: number;
  /** Share of the essence spent raising an item that salvage and a Trading Post sale give back. */
  readonly refund: Fix64;
  /** Per rarity: a level's own battle chest's essence before the level scaling (1 + level / chestLevelDivisor). */
  readonly chest: readonly number[];
  readonly chestLevelDivisor: number;
  /** A farm run's chest essence multiplier. */
  readonly farmMult: Fix64;
}

export class BalanceData {
  constructor(
    readonly schemaVersion: number,
    readonly tables: BalanceTables,
    readonly enemies: readonly EnemyArchetype[],
    readonly towers: readonly TowerArchetype[],
    readonly cards: CardsData,
    readonly economy: EconomyData,
    readonly chests: ChestsData,
    readonly meta: MetaData,
    readonly levels: readonly LevelData[],
    readonly gear: GearData = emptyGear(),
    readonly readiness: ReadinessData = emptyReadiness(),
    readonly orders: OrdersData = emptyOrders(),
    /** Elite affixes (DESIGN §5.1), `enemies.json` → `affixes`. */
    readonly affixes: readonly EnemyAffixDef[] = [],
    /** Gem sources, the Shop, the NPC market, Tribute, the upgrade queue (data/commerce.ts, ADR 0013). */
    readonly commerce: CommerceData = emptyCommerce(),
    /** The event calendar, the Weekly Trial and the Heroic tier (data/events.ts, DESIGN §10.5). */
    readonly events: EventsData = emptyEvents(),
    /** Achievements and tower mastery (data/achievements.ts, DESIGN §9.7). */
    readonly achievements: AchievementsData = emptyAchievements(),
    /** The Deals screen's offers, lucky chests, value baseline and VIP (data/offers.ts, docs/SHOP.md). */
    readonly offers: OffersData = emptyOffers(),
    /** VIP v2: levels, XP, streak, benefits incl. combat, daily bundles (data/vip.ts, docs/SHOP.md §8). */
    readonly vip: VipTable = emptyVipTable(),
    /** The Mystery Merchant, the Event shop and the VIP shop (data/shops.ts, docs/SHOP.md §9). */
    readonly shops: ShopsData = emptyShops(),
    /** Tier-4 specialisations: two branches per tower, their skills, the Ascend card's pacing (data/branches.ts, M21). */
    readonly branches: BranchesData = emptyBranches(),
    /** Iron challenges and Endless mode (data/modes.ts, docs/MODES.md). */
    readonly modes: ModesData = emptyModes(),
    /** Gold Research: the per-tower and Fortress trees (data/research.ts, docs/RESEARCH.md). */
    readonly research: ResearchData = emptyResearch(),
    /** Immune units and rare spawns (data/spawnRules.ts, M36). */
    readonly spawnRules: SpawnRulesData = emptySpawnRules(),
    /** The season pass "War Campaign": seasons, tiers, XP, the free and premium tracks (data/seasons.ts, MP-22). */
    readonly seasons: SeasonsData = emptySeasons(),
    /** The Trophy Hall: its unlock and the Armoury's weekly shelf (data/trophies.ts, MP-23). */
    readonly trophies: TrophiesData = emptyTrophies(),
    /** Pets: the roster, their jobs by Bond, the Bond XP curve (data/pets.ts, docs/PETS.md). */
    readonly pets: PetsData = emptyPets(),
  ) {}

  /** Stages on level `id`: its own count (level 1, the tutorial) or the stage curve's. */
  stagesOf(id: number): number { return this.level(id).stages ?? this.tables.stagesAt(id); }

  level(id: number): LevelData {
    if (!Number.isInteger(id) || id < 1 || id > this.levels.length) throw new RangeError(`no such level ${id}`);
    return this.levels[id - 1];
  }

  enemyIndex(id: string): number { return indexOf(this.enemies, id); }
  towerIndex(id: string): number { return indexOf(this.towers, id); }
  get baseTowerIndex(): number {
    const i = this.towers.findIndex((t) => t.isBase);
    if (i < 0) throw new Error("balance has no base tower archetype");
    return i;
  }
  shapeIndex(id: string): number { return indexOf(this.cards.shapes, id); }
  affixIndex(id: string): number { return indexOf(this.affixes, id); }
}

function indexOf<T extends { id: string }>(list: readonly T[], id: string): number {
  const i = list.findIndex((x) => x.id === id);
  if (i < 0) throw new Error(`Unknown id '${id}'`);
  return i;
}

// ------------------------------------------------------------------ loader

export const SUPPORTED_BALANCE_SCHEMA = 1;

type Json = any;

function fix(v: Json): Fix64 { return Fix64.parse(String(v)); }
function fixRaw(v: Json): Fix64 { return Fix64.fromRaw(BigInt(v)); }
function int(v: Json): number {
  if (typeof v === "number" && Number.isInteger(v)) return v;
  if (typeof v === "string" && /^-?\d+$/.test(v)) return Number(v);
  throw new TypeError(`expected integer, got ${JSON.stringify(v)}`);
}
function bool(v: Json): boolean {
  if (typeof v !== "boolean") throw new TypeError(`expected boolean, got ${JSON.stringify(v)}`);
  return v;
}
function str(v: Json): string {
  if (typeof v !== "string") throw new TypeError(`expected string, got ${JSON.stringify(v)}`);
  return v;
}
function req(o: Json, key: string): Json {
  if (o === null || typeof o !== "object" || !(key in o)) throw new Error(`Missing JSON member '${key}'`);
  return o[key];
}
function opt(o: Json, key: string, fallback: Json): Json {
  return o !== null && typeof o === "object" && key in o ? o[key] : fallback;
}

export function parseHeading(s: string): Heading {
  switch (s) {
    case "north": return Heading.North;
    case "east": return Heading.East;
    case "south": return Heading.South;
    case "west": return Heading.West;
    default: throw new Error(`Unknown heading '${s}'`);
  }
}

function parseGearStat(s: string): GearStat {
  const i = (GEAR_STAT_IDS as readonly string[]).indexOf(s);
  if (i < 0) throw new Error(`unknown gear stat '${s}'`);
  return i as GearStat;
}

function parseRarity(s: string): Rarity {
  const i = (RARITY_IDS as readonly string[]).indexOf(s);
  if (i < 0) throw new Error(`unknown rarity '${s}'`);
  return i as Rarity;
}

function parseStarter(v: Json | null): StarterChestData | null {
  if (v === null) return null;
  return { level: int(req(v, "level")), rarity: parseRarity(str(req(v, "rarity"))), gold: int(req(v, "gold")), gearCount: int(req(v, "gear")) };
}

function parseEarlyGear(v: Json | null): EarlyGearData | null {
  if (v === null) return null;
  return { throughLevel: int(req(v, "throughLevel")), forLevel: int(req(v, "forLevel")), minRarity: parseRarity(str(req(v, "minRarity"))), rareAtLevel: int(opt(v, "rareAtLevel", 0)) };
}

function emptyGear(): GearData {
  const zeros = RARITY_IDS.map(() => 0);
  return {
    slots: [], primaryBase: GEAR_STAT_IDS.map(() => Fix64.Zero), primaryRarityMult: RARITY_IDS.map(() => Fix64.One), affixCount: zeros, affixes: [],
    itemLevel: { growthPerLevel: Fix64.Zero, dropSpread: 0, maxLevel: 255 },
    essence: { raise: zeros, raiseLevelDivisor: 10, salvage: zeros, salvageLevelDivisor: 50, refund: Fix64.Zero, chest: zeros, chestLevelDivisor: 25, farmMult: Fix64.One },
    fusionMaxSigils: 2, fusionEssence: zeros, bindAboveRaised: 3,
    setPieces: 4, setMinRarity: Rarity.Epic, setDamage: Fix64.Zero, setRange: Fix64.Zero, mythicEffects: [], salvageBase: zeros,
    auctionMinPrice: zeros, powerRarity: zeros, affinity: new Map(), affinityMult: Fix64.One, retarget: new Map(),
  };
}

/** A bundle from before the level-gap bands: no gap rule (the percentages alone decide). */
const NO_LEVEL_GAP = 1_000_000;

function emptyReadiness(): ReadinessData {
  return { gearRequiredFrom: 1, readyGearPercent: 0, readyTowerPercent: 0, underGearPercent: 0, underTowerPercent: 0, readyTowerLevelsUnder: NO_LEVEL_GAP, underTowerLevelsUnder: NO_LEVEL_GAP, targets: [] };
}

/** No orders data (an old bundle, a test fixture): an empty pool, so nothing is ever issued. */
export function emptyOrders(): OrdersData {
  const none: CheckinRewardData = { resources: Fix64.Zero, speedUpMinutes: 0, chestRarity: null, gems: 0, sigil: null, sigils: 0 };
  return { unlockAfterLevel: 0, ordersPerDay: 0, chestsPerDay: Fix64.Zero, carryDays: 0, maxCarried: 0, freeRerollsPerDay: 0, weeklyGoal: 0, supplyBand: ChestBand.Win, weeklyBand: ChestBand.FirstClear, weeklyGems: 0, weeklyEssence: 0, pool: [], checkinSlots: [], welcomeBackAfterDays: 0, welcomeBack: none };
}

function parseUpgradeCostCurve(c: Json): UpgradeCostCurveData | null {
  if (c === null) return null;
  const curve = { fromLevel: int(req(c, "fromLevel")), rampLevels: int(req(c, "rampLevels")), step: fix(req(c, "step")), slopePerLevel: fix(req(c, "slopePerLevel")) };
  if (curve.rampLevels < 1) throw new Error("meta.upgradeCostCurve.rampLevels must be >= 1");
  return curve;
}

function parseBand(s: string): ChestBand {
  const i = BAND_IDS.indexOf(s as (typeof BAND_IDS)[number]);
  if (i < 0) throw new Error(`Unknown chest band '${s}'`);
  return i as ChestBand;
}

function parseCheckinReward(r: Json): CheckinRewardData {
  const chest = opt(r, "chest", null), sigil = opt(r, "sigil", null);
  return {
    resources: fix(opt(r, "resources", 0)), speedUpMinutes: int(opt(r, "speedUp", 0)), chestRarity: chest === null ? null : parseRarity(str(chest)), gems: int(opt(r, "gems", 0)),
    sigil: sigil === null ? null : parseRarity(str(sigil)), sigils: sigil === null ? 0 : int(opt(r, "sigils", 1)),
  };
}

function parseOrders(o: Json): OrdersData {
  const ci = req(o, "checkin");
  return {
    unlockAfterLevel: int(req(o, "unlockAfterLevel")), ordersPerDay: int(req(o, "ordersPerDay")), chestsPerDay: fix(req(o, "chestsPerDay")),
    carryDays: int(req(o, "carryDays")), maxCarried: int(req(o, "maxCarried")), freeRerollsPerDay: int(req(o, "freeRerollsPerDay")), weeklyGoal: int(req(o, "weeklyGoal")),
    supplyBand: parseBand(str(req(o, "supplyBand"))), weeklyBand: parseBand(str(req(o, "weeklyBand"))), weeklyGems: int(opt(o, "weeklyGems", 0)), weeklyEssence: int(opt(o, "weeklyEssence", 0)),
    pool: (req(o, "pool") as Json[]).map((e) => {
      const kind = str(req(e, "kind"));
      if (kind !== "hub" && kind !== "battle") throw new Error(`order '${e.id}': unknown kind '${kind}'`);
      return { id: str(req(e, "id")), kind, metric: str(req(e, "metric")), target: int(req(e, "target")), size: fix(req(e, "size")), speedUpMinutes: int(req(e, "speedUp")), minLevel: int(req(e, "minLevel")), title: str(req(e, "title")), ...parseOrderSupply(e) };
    }),
    checkinSlots: (req(ci, "slots") as Json[]).map(parseCheckinReward),
    welcomeBackAfterDays: int(req(ci, "welcomeBackAfterDays")), welcomeBack: parseCheckinReward(req(ci, "welcomeBack")),
  };
}

/** An order's optional `supply` (MP-06). */
function parseOrderSupply(e: Json): { supply?: SupplyId } {
  const v = opt(e, "supply", undefined);
  if (v === undefined) return {};
  if (!isSupplyId(v)) throw new Error(`order '${e.id}': unknown supply '${String(v)}'`);
  return { supply: v };
}

function parseReadiness(r: Json, towers: readonly TowerArchetype[], gear: GearData): ReadinessData {
  const towerIdx = (id: string): number => {
    const i = towers.findIndex((t) => t.id === id && !t.isBase);
    if (i < 0) throw new Error(`readiness seat on unknown tower '${id}'`);
    return i;
  };
  return {
    gearRequiredFrom: int(req(r, "gearRequiredFrom")), readyGearPercent: int(req(r, "readyGearPercent")), readyTowerPercent: int(req(r, "readyTowerPercent")),
    underGearPercent: int(req(r, "underGearPercent")), underTowerPercent: int(req(r, "underTowerPercent")),
    readyTowerLevelsUnder: int(opt(r, "readyTowerLevelsUnder", NO_LEVEL_GAP)), underTowerLevelsUnder: int(opt(r, "underTowerLevelsUnder", NO_LEVEL_GAP)),
    targets: (req(r, "targets") as Json[]).map((t) => ({
      gear: int(req(t, "gear")), towers: int(req(t, "towers")),
      seats: gear.slots.map((s) => (s.target === GearTarget.Tower ? (opt(opt(t, "seats", {}), s.id, []) as Json[]).map((id) => towerIdx(str(id))) : [])),
    })),
  };
}

function parseGear(g: Json): GearData {
  const set = req(g, "set"), power = req(g, "powerScore"), il = req(g, "itemLevel"), es = req(g, "essence");
  const byRarity = (o: Json) => RARITY_IDS.map((r) => int(req(o, r)));
  return {
    slots: (req(g, "slots") as Json[]).map((s, index) => ({ index, id: str(req(s, "id")), name: str(req(s, "name")), target: str(req(s, "target")) === "base" ? GearTarget.Base : GearTarget.Tower, primary: parseGearStat(str(req(s, "primary"))) })),
    primaryBase: GEAR_STAT_IDS.map((id) => fix(opt(req(g, "primaryBase"), id, 0))),
    primaryRarityMult: RARITY_IDS.map((r) => fix(req(req(g, "primaryRarityMult"), r))),
    affixCount: RARITY_IDS.map((r) => int(req(req(g, "affixCount"), r))),
    affixes: (req(g, "affixes") as Json[]).map((a) => ({ stat: parseGearStat(str(req(a, "id"))), value: fix(req(a, "value")) })),
    itemLevel: { growthPerLevel: fix(req(il, "growthPerLevel")), dropSpread: int(req(il, "dropSpread")), maxLevel: int(req(il, "maxLevel")) },
    essence: {
      raise: byRarity(req(es, "raise")), raiseLevelDivisor: int(req(es, "raiseLevelDivisor")),
      salvage: byRarity(req(es, "salvage")), salvageLevelDivisor: int(req(es, "salvageLevelDivisor")), refund: fix(req(es, "refund")),
      chest: byRarity(req(es, "chest")), chestLevelDivisor: int(req(es, "chestLevelDivisor")), farmMult: fix(req(es, "farmMult")),
    },
    fusionMaxSigils: int(req(req(g, "fusion"), "maxSigils")),
    fusionEssence: RARITY_IDS.map((r) => int(opt(opt(req(g, "fusion"), "essence", {}), r, 0))),
    bindAboveRaised: int(req(g, "bindAboveRaised")),
    setPieces: int(req(set, "pieces")), setMinRarity: parseRarity(str(req(set, "minRarity"))), setDamage: fix(req(set, "damage")), setRange: fix(req(set, "range")),
    mythicEffects: (req(g, "mythicEffects") as Json[]).map((m, index) => ({ index, id: str(req(m, "id")), name: str(req(m, "name")), towerId: str(req(m, "tower")), description: str(req(m, "description")), value: fix(opt(m, "value", 0)) })),
    salvageBase: byRarity(req(g, "salvageBase")),
    auctionMinPrice: byRarity(req(g, "auctionMinPrice")),
    powerRarity: (req(power, "rarity") as Json[]).map(int),
    affinity: new Map(Object.entries(opt(g, "affinity", {}) as Record<string, Json>).filter(([k]) => !k.startsWith("_")).map(([k, v]) => [k, parseGearStat(str(v))])),
    affinityMult: fix(opt(g, "affinityMult", 1)),
    retarget: new Map(Object.entries(opt(g, "retarget", {}) as Record<string, Json>).filter(([k]) => !k.startsWith("_")).map(([k, v]) => [k, (v as Json[]).map((s) => parseGearStat(str(s)))])),
  };
}

function parseTroops(t: Json): TroopsData {
  return {
    hp: fix(opt(t, "hp", "30")), damage: fix(opt(t, "damage", "4")), attackIntervalTicks: int(opt(t, "attackIntervalTicks", 20)), cap: int(opt(t, "cap", 12)),
    speedCellsPerSecond: fix(opt(t, "speedCellsPerSecond", "1.5")), engageRange: fix(opt(t, "engageRange", "3")), statGrowth: fix(opt(t, "statGrowth", "1.03")),
    spawnIntervalTicksAtL1: int(opt(t, "spawnIntervalTicksAtL1", 160)), spawnIntervalTicksAtMaxLevel: int(opt(t, "spawnIntervalTicksAtMaxLevel", 60)),
    enemyHitFraction: fix(opt(t, "enemyHitFraction", "0.05")), enemyAttackIntervalTicks: int(opt(t, "enemyAttackIntervalTicks", 20)), blockRange: fix(opt(t, "blockRange", "0.6")),
    baseTierBonus: fix(opt(t, "baseTierBonus", "0")), formationSpacing: fix(opt(t, "formationSpacing", "0.35")), formationRows: int(opt(t, "formationRows", 3)),
    formationLateralSpacing: fix(opt(t, "formationLateralSpacing", opt(t, "formationSpacing", "0.35"))),
  };
}

function parseTileKind(s: string): TileKind {
  const map: Record<string, TileKind> = { normal: TileKind.Normal, mud: TileKind.Mud, bridge: TileKind.Bridge, toll: TileKind.Toll, fireTrap: TileKind.FireTrap };
  if (!(s in map)) throw new Error(`Unknown tileKind '${s}'`);
  return map[s];
}

function parseDamageType(s: string): DamageType {
  const map: Record<string, DamageType> = { physical: DamageType.Physical, magic: DamageType.Magic, burn: DamageType.Burn };
  if (!(s in map)) throw new Error(`Unknown damageType '${s}'`);
  return map[s];
}

function parseAffixes(list: Json[], eidx: (id: string) => number): EnemyAffixDef[] {
  return list.map((a, index) => {
    const into = str(opt(a, "splitInto", ""));
    return {
      index, id: str(req(a, "id")), name: str(req(a, "name")),
      physicalTaken: fix(opt(a, "physicalTaken", 1)), magicTaken: fix(opt(a, "magicTaken", 1)), burnTaken: fix(opt(a, "burnTaken", 1)),
      slowMult: fix(opt(a, "slowMult", 1)), splitInto: into === "" ? -1 : eidx(into), splitCount: int(opt(a, "splitCount", 0)),
      hpMult: fix(opt(a, "hpMult", 1)), pointsMult: fix(opt(a, "pointsMult", 1)),
    };
  });
}

function parseEliteRule(v: Json, affixes: readonly EnemyAffixDef[], where: string): EliteRule {
  const id = str(req(v, "affix"));
  const affix = affixes.findIndex((a) => a.id === id);
  if (affix < 0) throw new Error(`${where}: unknown affix '${id}'`);
  const every = int(req(v, "every")), fromStage = int(opt(v, "fromStage", 1));
  if (every < 1 || fromStage < 1) throw new Error(`${where}: elite rule needs every >= 1 and fromStage >= 1`);
  return { affix, every, fromStage };
}

/**
 * A level's elite rules: its own `elite` entry (null = none, overriding its block), else the `levels.json` → `elites`
 * block that covers it while that table is `enabled`. Off by default: WP3b turns the blocks on and tunes them.
 */
/** A level's campaign HP multiplier (LevelData.hpMult): its own, else the `levels.json` → `hpMult` table's from its level. */
function parseLevelHpMult(doc: Json, l: Json): Fix64 {
  const own = opt(l, "hpMult", null);
  const table = opt(doc, "hpMult", null);
  const v = own !== null ? fix(own) : table !== null && int(req(l, "id")) >= int(opt(table, "fromLevel", 1)) ? fix(req(table, "value")) : Fix64.One;
  if (!v.isPositive()) throw new Error(`level ${int(req(l, "id"))}: hpMult must be > 0`);
  return v;
}

function parseLevelElites(doc: Json, l: Json, affixes: readonly EnemyAffixDef[]): EliteRule[] {
  const id = int(req(l, "id"));
  if (l !== null && typeof l === "object" && "elite" in l) return l.elite === null ? [] : [parseEliteRule(l.elite, affixes, `level ${id}`)];
  const table = opt(doc, "elites", null);
  if (table === null || !bool(opt(table, "enabled", false))) return [];
  const block = (opt(table, "blocks", []) as Json[]).find((b) => int(req(b, "from")) <= id && id <= int(req(b, "to")));
  return block ? [parseEliteRule(block, affixes, `level ${id}`)] : [];
}

/** Builds BalanceData from the generated bundle (balance/generated/balance.json), parsed or as text. */
export function loadBalance(source: string | Json): BalanceData {
  const root: Json = typeof source === "string" ? JSON.parse(source) : source;
  const schema = int(req(root, "schemaVersion"));
  if (schema !== SUPPORTED_BALANCE_SCHEMA) throw new Error(`Unsupported balance schemaVersion ${schema}`);
  const t = req(root, "tables");
  const tables = new BalanceTables(
    (req(t, "stagePower") as Json[]).map(fixRaw),
    (req(t, "levelBase") as Json[]).map(fixRaw),
    (req(t, "levelMult") as Json[]).map(fixRaw),
    (req(t, "timerSeconds") as Json[]).map(int),
    (req(t, "roadPaceTiles") as Json[]).map(int),
    (req(t, "stages") as Json[]).map(int),
    (opt(t, "upgradeCost", null) === null ? req(t, "levelMult") : req(t, "upgradeCost") as Json[]).map(fixRaw),
  );
  const enemyIds = (req(req(root, "enemies"), "enemies") as Json[]).map((e) => str(req(e, "id")));
  const enemies: EnemyArchetype[] = (req(req(root, "enemies"), "enemies") as Json[]).map((e, index) => ({
    index, id: str(req(e, "id")), name: str(req(e, "name")), hp: fix(req(e, "hp")), speedCellsPerSecond: fix(req(e, "speed")),
    armour: fix(req(e, "armour")), points: int(req(e, "points")), leakDamage: int(req(e, "leakDamage")), isAir: bool(req(e, "air")), unlockLevel: int(req(e, "unlockLevel")),
    healRadius: fix(opt(e, "healRadius", 0)), healFractionPerSecond: fix(opt(e, "healFractionPerSecond", 0)),
    boostThreshold: fix(opt(e, "boostThreshold", 0)), boostSpeedMult: fix(opt(e, "boostSpeedMult", 1)),
    boostDurationTicks: int(opt(e, "boostDurationTicks", 0)), boostCooldownTicks: int(opt(e, "boostCooldownTicks", 0)),
    bombDisableTicks: int(opt(e, "bombDisableTicks", 0)), bombRange: fix(opt(e, "bombRange", 0)),
    shieldRadius: fix(opt(e, "shieldRadius", 0)), shieldArmour: fix(opt(e, "shieldArmour", 0)), bossMechanic: parseBossMechanic(str(opt(e, "bossMechanic", ""))),
    heavy: bool(opt(e, "heavy", false)),
    abilities: parseEnemyAbilities(e, enemyIds),
    rareChapter: bool(opt(e, "rare", false)) ? int(req(e, "chapter")) : 0,
    legend: bool(opt(e, "legend", false)),
    wildPet: String(opt(e, "wildPet", "")),
    model: String(opt(e, "model", "")),
  }));
  const towers: TowerArchetype[] = (req(req(root, "towers"), "towers") as Json[]).map((tw, index) => ({
    index, id: str(req(tw, "id")), name: str(req(tw, "name")), range: fix(req(tw, "range")), damage: fix(req(tw, "damage")),
    fireIntervalTicks: int(req(tw, "fireIntervalTicks")), targetsGround: bool(req(tw, "targetsGround")), targetsAir: bool(req(tw, "targetsAir")),
    splashRadius: fix(req(tw, "splashRadius")), damageType: parseDamageType(str(req(tw, "damageType"))), unlockLevel: int(req(tw, "unlockLevel")),
    onePerMap: bool(req(tw, "onePerMap")),
    isBase: bool(opt(tw, "isBase", false)),
    tiers: (req(tw, "tiers") as Json[]).map((tier) => ({ damageMult: fix(req(tier, "damageMult")), rangeBonus: fix(req(tier, "rangeBonus")), soldierArmour: fix(opt(tier, "soldierArmour", 0)) })),
    slowFraction: fix(opt(tw, "slowFraction", 0)), slowDurationTicks: int(opt(tw, "slowDurationTicks", 0)),
    burnDamagePerSecond: fix(opt(tw, "burnDamagePerSecond", 0)), burnDurationTicks: int(opt(tw, "burnDurationTicks", 0)), burnMaxStacks: int(opt(tw, "burnMaxStacks", 1)),
    chainTargets: int(opt(tw, "chainTargets", 0)),
    area: bool(opt(tw, "area", fix(req(tw, "damage")).isPositive() && (fix(req(tw, "splashRadius")).isPositive() || int(opt(tw, "chainTargets", 0)) > 1))),
    chainRange: fix(opt(tw, "chainRange", 0)), chainFalloff: fix(opt(tw, "chainFalloff", 1)),
    pierceCount: int(opt(tw, "pierceCount", 0)), pierceWidth: fix(opt(tw, "pierceWidth", 0)),
    shredPerHit: fix(opt(tw, "shredPerHit", 0)), shredMax: int(opt(tw, "shredMax", 0)),
    auraDamageBonus: fix(opt(tw, "auraDamageBonus", 0)), auraPointsBonus: fix(opt(tw, "auraPointsBonus", 0)),
    minRange: fix(opt(tw, "minRange", 0)), shellFlightTicks: int(opt(tw, "shellFlightTicks", 0)),
    soldiers: int(opt(tw, "soldiers", 0)), soldierHp: fix(opt(tw, "soldierHp", 0)), soldierReach: fix(opt(tw, "soldierReach", 0)), soldierRespawnTicks: int(opt(tw, "soldierRespawnTicks", 0)),
    rampMax: fix(opt(tw, "rampMax", 1)), rampTicks: int(opt(tw, "rampTicks", 0)),
    hexDamageTaken: fix(opt(tw, "hexDamageTaken", 0)), hexHealCut: fix(opt(tw, "hexHealCut", 0)),
    levelGrowth: parseLevelGrowth(opt(tw, "levelGrowth", {})),
  }));
  const c = req(root, "cards");
  const shapes: PathShape[] = (req(c, "shapes") as Json[]).map((s, index) => ({
    index, id: str(req(s, "id")), name: str(req(s, "name")),
    tiles: (req(s, "tiles") as Json[]).map((p) => ({ x: int(p[0]), y: int(p[1]) })),
    weight: int(req(s, "weight")), unlockLevel: int(req(s, "unlockLevel")), tileKind: parseTileKind(str(req(s, "tileKind"))), maxPerLevel: int(req(s, "maxPerLevel")),
    paceCost: int(opt(s, "paceCost", (req(s, "tiles") as Json[]).length)),
  }));
  const cards = new CardsData(
    int(req(c, "offerSize")), int(req(c, "openingPathOffers")), int(req(c, "openingTowerOffers")),
    int(req(c, "offerPointsBase")), fix(req(c, "offerPointsGrowth")),
    int(req(c, "rerollEveryWaves")), int(req(c, "rerollBank")),
    int(req(c, "maxSameTowerPerOffer")), int(req(c, "towerCardWeight")), shapes,
    int(req(c, "maxTowerCardsPerOffer")), (req(c, "towerOfferIntervals") as Json[]).map(int), int(req(c, "upgradeOfferInterval")),
    { path: int(req(req(c, "pickPointsCost"), "path")), tower: int(req(req(c, "pickPointsCost"), "tower")), upgrade: int(req(req(c, "pickPointsCost"), "upgrade")) },
    fix(opt(opt(c, "tiles", {}), "mudSpeedMult", "0.7")), fix(opt(opt(c, "tiles", {}), "tollPointsMult", "1.1")),
    { firstInterval: int(req(req(c, "roadPace"), "firstInterval")), intervalStepTiles: int(req(req(c, "roadPace"), "intervalStepTiles")), maxInterval: int(req(req(c, "roadPace"), "maxInterval")) },
    { weight: int(opt(opt(c, "moveCard", {}), "weight", 0)), minTowers: int(opt(opt(c, "moveCard", {}), "minTowers", 3)), pointsCost: int(opt(opt(c, "moveCard", {}), "pointsCost", 0)) },
  );
  const ec = req(root, "economy");
  const economy: EconomyData = {
    baseHp: int(req(ec, "baseHp")), betweenStageTicks: int(req(ec, "betweenStageTicks")),
    firstStageDelayTicks: int(req(ec, "firstStageDelayTicks")), bossDelayTicks: int(req(ec, "bossDelayTicks")),
    bossHpMult: fix(req(ec, "bossHpMult")), bossPoints: int(req(ec, "bossPoints")), bossLeakDamage: int(req(ec, "bossLeakDamage")),
    earlyCallPointsPerSecond: fix(opt(ec, "earlyCallPointsPerSecond", 0)), skipOfferPoints: int(opt(ec, "skipOfferPoints", 0)),
    areaVsHeavyMult: fix(opt(ec, "areaVsHeavyMult", 1)),
    checkpointEveryStages: int(opt(ec, "checkpointEveryStages", 0)),
    troops: parseTroops(opt(ec, "troops", {})),
  };
  const gear = "gear" in (root as object) ? parseGear(req(root, "gear")) : emptyGear();
  const ch = req(root, "chests");
  const permille = (v: Json): number => Math.round(Number(v) * 1000);
  const chests: ChestsData = {
    bandWeights: BAND_IDS.map((band) => {
      const bands = req(ch, "bands"), from = band in bands ? band : BAND_FALLBACK[band] ?? band;
      return RARITY_IDS.map((r) => permille(req(req(bands, from), r)));
    }),
    rarityMult: RARITY_IDS.map((r) => fix(req(req(ch, "rarityMult"), r))),
    resourceBase: int(req(ch, "resourceBase")), resourcePerLevel: int(req(ch, "resourcePerLevel")),
    gearCount: RARITY_IDS.map((r) => { const v = opt(opt(ch, "gearCount", {}), r, [1, 1]) as Json[]; return [int(v[0]), int(v[1])] as const; }),
    speedUpMinutes: RARITY_IDS.map((r) => int(opt(opt(ch, "speedUpMinutes", {}), r, 0))),
    firstClearExtraGear: int(opt(ch, "firstClearExtraGear", 0)),
    farmResourceMult: fix(opt(ch, "farmResourceMult", "1")),
    farmExtraGearPercent: int(opt(ch, "farmExtraGearPercent", 0)),
    lateFirstClearFrom: int(opt(opt(ch, "lateFirstClear", {}), "fromLevel", 0)),
    lateFirstClearBand: parseBand(str(opt(opt(ch, "lateFirstClear", {}), "band", "firstClear"))),
    lateFirstClearRarityBump: int(opt(opt(ch, "lateFirstClear", {}), "rarityBump", 0)),
    starter: parseStarter(opt(ch, "starter", null)),
    earlyResourceMult: fix(opt(opt(ch, "earlyResources", {}), "mult", "1")),
    earlyFullThroughLevel: int(opt(opt(ch, "earlyResources", {}), "fullThroughLevel", 0)),
    earlyTaperToLevel: int(opt(opt(ch, "earlyResources", {}), "taperToLevel", 0)),
    earlyGear: parseEarlyGear(opt(ch, "earlyGear", null)),
    climbFrom: RARITY_IDS.map((r) => int(opt(opt(opt(ch, "rarityClimb", {}), "fromLevel", {}), r, 1))),
    climbFull: RARITY_IDS.map((r) => int(opt(opt(opt(ch, "rarityClimb", {}), "fullLevel", {}), r, 1))),
  };
  const enemyIndex = new Map(enemies.map((e) => [e.id, e.index]));
  const eidx = (id: string): number => {
    const i = enemyIndex.get(id);
    if (i === undefined) throw new Error(`unknown enemy '${id}'`);
    return i;
  };
  const affixes = parseAffixes(opt(req(root, "enemies"), "affixes", []), eidx);
  const levels: LevelData[] = (req(req(root, "levels"), "levels") as Json[]).map((l) => ({
    id: int(req(l, "id")), name: str(req(l, "name")), width: int(req(l, "width")), height: int(req(l, "height")),
    baseX: int(req(l, "base")[0]), baseY: int(req(l, "base")[1]), baseHeading: parseHeading(str(req(l, "baseHeading"))),
    blocked: (req(l, "blocked") as Json[]).map((p) => ({ x: int(p[0]), y: int(p[1]) })),
    chasm: (opt(l, "chasm", []) as Json[]).map((p) => ({ x: int(p[0]), y: int(p[1]) })),
    groups: (req(req(l, "waves"), "groups") as Json[]).map((g) => new SpawnGroup(eidx(str(req(g, "enemy"))), int(req(g, "baseCount")), fix(req(g, "countPerStage")), int(req(g, "intervalTicks")), int(req(g, "delayTicks")), int(opt(g, "fromStage", 1)),
      int(opt(g, "untilStage", 0)), int(opt(g, "everyStages", 1)), int(opt(g, "repeats", 1)), int(opt(g, "repeatGapTicks", 0)), fix(opt(g, "speedMult", "1")))),
    bossEnemyIndex: eidx(str(req(req(l, "waves"), "bossEnemy"))),
    stages: opt(l, "stages", null) === null ? null : int(req(l, "stages")),
    stagePower: opt(l, "stagePower", null) === null ? null : (req(l, "stagePower") as Json[]).map(fixRaw),
    overlapTicks: int(opt(l, "overlapTicks", 0)),
    bossHpMult: opt(req(l, "waves"), "bossHpMult", null) === null ? null : fix(req(req(l, "waves"), "bossHpMult")),
    bossDelayTicks: opt(req(l, "waves"), "bossDelayTicks", null) === null ? null : int(req(req(l, "waves"), "bossDelayTicks")),
    terrain: parseLevelTerrain(opt(l, "terrain", null)),
    challenge: parseChallenge(opt(l, "challenge", null), towers, int(req(l, "id"))),
    elites: parseLevelElites(req(root, "levels"), l, affixes),
    starHpPercent: parseStarHpPercent(opt(l, "starHpPercent", DEFAULT_STAR_HP_PERCENT), int(req(l, "id"))),
    hpMult: parseLevelHpMult(req(root, "levels"), l),
  }));
  const mt = req(root, "meta");
  const meta: MetaData = {
    upgradeGoldBase: int(req(mt, "upgradeGoldBase")),
    reductionSecondsPerLevel: int(req(mt, "reductionSecondsPerLevel")), reductionsPerDay: int(req(mt, "reductionsPerDay")),
    powerTowerWeight: int(req(mt, "powerTowerWeight")), powerBaseWeight: int(req(mt, "powerBaseWeight")), builderCostsGems: [],
    cancelRefundFraction: fix(opt(mt, "cancelRefundFraction", "0.5")),
    upgradeCostCurve: parseUpgradeCostCurve(opt(mt, "upgradeCostCurve", null)),
  };
  const readiness = "readiness" in (root as object) ? parseReadiness(req(root, "readiness"), towers, gear) : emptyReadiness();
  const orders = "orders" in (root as object) ? parseOrders(req(root, "orders")) : emptyOrders();
  return new BalanceData(schema, tables, enemies, towers, cards, economy, chests, meta, levels, gear, readiness, orders, affixes, parseCommerce(root), parseEvents(root, affixes), parseAchievements(root), parseOffers(root), parseVipTable(root), parseShops(root), parseBranches(root, towers.map((x) => x.id)), parseModes(root), parseResearch(root, towers.map((x) => x.id)), parseSpawnRules(root), parseSeasons(root), parseTrophies(root), parsePets(root, tables.maxLevel));
}

function parseLevelGrowth(g: Json): TowerLevelGrowth {
  return {
    slowFraction: fix(opt(g, "slowFraction", 0)), slowTicks: int(opt(g, "slowTicks", 0)), slowFractionMax: fix(opt(g, "slowFractionMax", 1)),
    auraDamageBonus: fix(opt(g, "auraDamageBonus", 0)), hexDamageTaken: fix(opt(g, "hexDamageTaken", 0)),
  };
}
